import { ChangeDetectionStrategy, Component } from '@angular/core';

import { LearnSectionComponent } from '@ledgerly/landing/blog';

import { HeroComponent } from '../hero/hero.component';
import { StepsComponent } from '../steps/steps.component';
import { NoBankComponent } from '../no-bank/no-bank.component';
import { FeaturesComponent } from '../features/features.component';
import { PrivacySectionComponent } from '../privacy/privacy-section.component';
import { PricingComponent } from '../pricing/pricing.component';
import { FaqsComponent } from '../faqs/faqs.component';

/**
 * The page.
 *
 * The order is an argument, not a list of sections. A reader arrives asking
 * what this is, then how it works, then what the catch is, and only then are
 * they willing to read about four tabs.
 *
 * So: the number the product exists to produce, then the three steps that get
 * you there, then the thing that will otherwise stop them dead, which is that
 * there is no bank connection. Answering that here rather than in the FAQ is
 * deliberate. A reader who wants automatic import should leave at this point
 * rather than after installing, and a reader who does not now has the reason
 * the privacy claim below is true.
 *
 * Then the four tabs, then the claim about your data, then what it costs, then
 * the questions that are left.
 *
 * The reading list used to sit between the price and the FAQ. It was the only
 * thing on the page offering a way off it, and it sat at the point where the
 * reader has just seen the price. It now sits after the FAQ, where the
 * argument is finished. It disappears entirely in a language with no posts.
 *
 * The closing ask is not here. It lives in the footer, which carries it on the
 * landing page and drops it everywhere else, so the page ends on one band
 * rather than a band followed by a second block of chrome.
 *
 * The rhythm alternates band and ground: hero on the band, the middle of the
 * argument on the ground, privacy on the band, the rest on the ground, footer
 * on the band. The band is the one saturated field in the system, so it is
 * spent on the sections that carry the most weight and on nothing else.
 *
 * The `reviews` component is gone rather than commented out. It will come back
 * when there are real reviews; invented ones would be the same integrity
 * failure as an invented privacy claim, in a different register. The trust row
 * in the hero exists under the same rule: three facts the product already has
 * to stand behind, and no numbers until the stores report real ones.
 */
@Component({
  selector: 'app-landing-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeroComponent,
    StepsComponent,
    NoBankComponent,
    FeaturesComponent,
    PrivacySectionComponent,
    PricingComponent,
    LearnSectionComponent,
    FaqsComponent,
  ],
  template: `
    <app-hero />
    <app-steps />
    <app-no-bank />
    <app-features />
    <app-privacy-section />
    <app-pricing />
    <app-faqs />
    <app-learn-section />
  `,
})
export class LandingPageComponent {}
